import { useCallback, useMemo } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { TokenizeCardErrors } from "@hopper-b2b/types";

// Stolen from apps/virgin-au/src/modules/hotels-checkout/components/CheckoutPayment/components/PaymentMethod/components/CardPaymentForm/types/spreedly/on/onErrors.ts
const SpreedlyError = {
  AccountInactive: "errors.account_inactive", // A non-test card number was submitted against a test (unpaid) account. This can also occur when an invalid card number is submitted while testing as Spreedly assumes all unknown card numbers are real.
  EnvironmentKeyParameterRequired: "errors.environment_key_parameter_required", // An environment was not specified. Specify one when initializing iFrame: Spreedly.init
  InvalidEnvironmentKeyParameter: "errors.invalid_environment_key_parameter", // The specified environment key is not valid. Check the supplied environment key value and re-submit.
  Blank: "errors.blank", // The payment method field specified by the attribute property of this error was submitted without a value, which is required by Spreedly. Display an appropriate error message and prompt the user to fill in the specified fields.
  Invalid: "errors.invalid", // The payment method field specified by the attribute property of this error was submitted with an invalid value (e.g., a month value of “13”). Display an appropriate error message and prompt the user to correct the specified fields.
  BlankCardType: "errors.blank_card_type", // The credit card number was invalid; we were unable to determine the card type.
  Expired: "errors.expired", // The payment method month and year indicates that it is expired. Display an appropriate error message and prompt the user to enter a non-expired payment method.
  UnknownReferrer: "errors.unknown_referrer", // Spreedly could not determine the source (referring URL) of the form submission. A referring URL is required to identify MITM attacks.
  InvalidReferrer: "errors.invalid_referrer", // The form submission did not originate from the Spreedly payment frame and has been rejected. This is a protection against MITM attempts.
  Configuration: "errors.configuration", // If iFrame is configured to recache but tokenizeCreditCard is invoked, or iFrame is configured to tokenize but recache is invoked.
};

export const useTranslatedSpreedlyError = () => {
  const { t } = useI18nContext();
  const getTranslatedSpreedlyError = useCallback(
    (error: TokenizeCardErrors | undefined): string | undefined => {
      if (error == null) {
        return;
      }

      // May need more errors to handle
      switch (error.key) {
        case SpreedlyError.Invalid: {
          switch (error.attribute) {
            case "year":
              return t("spreedly.error.invalidYear");
            case "month":
              return t("spreedly.error.invalidMonth");
            case "card_type":
              return t("spreedly.error.invalidCardType");
            case "first_name":
              return t("spreedly.error.invalidFirstName");
            case "last_name":
              return t("spreedly.error.invalidLastName");
            case "number":
              return t("spreedly.error.invalidNumber");
          }
          break;
        }
        case SpreedlyError.Blank: {
          switch (error.attribute) {
            case "first_name":
              return t("spreedly.error.blankFirstName");
            case "last_name":
              return t("spreedly.error.blankLastName");
          }
          break;
        }
        case SpreedlyError.BlankCardType: {
          return t("spreedly.error.blankCardType");
        }
      }
    },
    [t]
  );

  return useMemo(
    () => ({ getTranslatedSpreedlyError }),
    [getTranslatedSpreedlyError]
  );
};
